import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { Check, Crown, Zap, Wrench, Rocket, Star, ArrowRight, Loader2, Settings, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";

const PLANS = [
  { id: "starter", name: "Starter", icon: Zap, price: 99, credits: 130, perCredit: 0.76, save: 15, accent: false },
  { id: "pro", name: "Pro", icon: Rocket, price: 199, credits: 290, perCredit: 0.686, save: 23, accent: true },
  { id: "atelier", name: "Atelier", icon: Wrench, price: 399, credits: 650, perCredit: 0.614, save: 31, accent: false },
  { id: "elite", name: "Élite", icon: Crown, price: 3990, monthly: 332, credits: 8400, perCredit: 0.475, save: 47, annual: true, accent: false },
];

const PlanCard = ({ plan, onSubscribe, busy, t, isActive }) => {
  const Icon = plan.icon;
  const perks = t(`sub.plans.${plan.id}.perks`) || [];
  return (
    <div
      data-testid={`plan-${plan.id}`}
      className={`relative glass rounded-md p-6 flex flex-col ${isActive ? "ring-2 ring-signal-success" : plan.accent ? "ring-2 ring-aurora-cyan shadow-glow" : plan.annual ? "ring-1 ring-aurora-purple/50" : ""}`}
    >
      {plan.accent && <div className="m-stripe-bar absolute top-0 left-0" />}
      {isActive ? (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-signal-success text-black font-mono text-[9px] uppercase tracking-widest rounded-sm flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Formule actuelle</div>
      ) : plan.accent ? <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-aurora-cyan text-black font-mono text-[9px] uppercase tracking-widest rounded-sm flex items-center gap-1"><Star className="w-3 h-3 fill-black" /> {t("sub.popular")}</div> : null}
      {plan.annual && !isActive && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-aurora-purple text-white font-mono text-[9px] uppercase tracking-widest rounded-sm">{t("sub.bestValue")}</div>}

      <div className="flex items-center gap-2 mb-1">
        <div className={`w-9 h-9 grid place-items-center rounded-sm border ${plan.accent ? "border-aurora-cyan/40 bg-aurora-cyan/10 text-aurora-cyan" : plan.annual ? "border-aurora-purple/40 bg-aurora-purple/10 text-aurora-purple" : "border-white/10 text-zinc-300"}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <div className="font-display text-xl font-bold uppercase tracking-tight leading-none">{plan.name}</div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{t(`sub.plans.${plan.id}.tag`)}</div>
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-end gap-1">
          <span className="font-display text-4xl font-bold">{plan.price.toLocaleString("fr-FR")}€</span>
          <span className="text-zinc-500 text-sm mb-1">{t("sub.ht")} {plan.annual ? t("sub.perYear") : t("sub.perMonth")}</span>
        </div>
        {plan.annual && <div className="font-mono text-[11px] text-aurora-purple">{t("sub.monthsFree", { m: plan.monthly })}</div>}
        <div className="mt-1 flex items-center gap-2">
          <span className="font-mono text-sm text-aurora-cyan font-bold">{plan.credits.toLocaleString("fr-FR")} {plan.annual ? t("sub.creditsYr") : t("sub.creditsMo")}</span>
          <span className="font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-signal-success/15 text-signal-success">‑{plan.save}%</span>
        </div>
        <div className="font-mono text-[10px] text-zinc-500 mt-0.5">{t("sub.perCredit", { v: plan.perCredit.toFixed(3).replace(".", ",") })}</div>
      </div>

      <div className="mt-5 space-y-2.5 flex-1">
        {perks.map((p) => (
          <div key={p} className="flex items-start gap-2 text-sm text-zinc-300">
            <Check className="w-4 h-4 text-signal-success shrink-0 mt-0.5" /> {p}
          </div>
        ))}
      </div>

      <button
        data-testid={`subscribe-${plan.id}`}
        onClick={() => onSubscribe(plan)}
        disabled={busy === plan.id || isActive}
        className={`mt-6 w-full flex items-center justify-center gap-2 py-3 font-display font-bold uppercase tracking-wider text-sm rounded-sm transition-colors disabled:opacity-60 ${isActive ? "bg-signal-success/20 text-signal-success border border-signal-success/40 cursor-default" : plan.accent ? "bg-aurora-cyan text-black hover:bg-cyan-300" : plan.annual ? "bg-aurora-purple text-white hover:bg-purple-500" : "border border-white/15 text-white hover:border-aurora-cyan/60"}`}
      >
        {isActive ? <><ShieldCheck className="w-4 h-4" /> Actif</> : busy === plan.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ArrowRight className="w-4 h-4" /> {t("sub.subscribe")}</>}
      </button>
    </div>
  );
};

export const Subscriptions = () => {
  const { t } = useI18n();
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(null);
  const [active, setActive] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const compare = t("sub.compare") || [];

  const loadMine = async () => {
    try { const { data } = await api.get("/subscriptions/me"); setActive(data.subscription || null); } catch { /* noop */ }
  };

  useEffect(() => { loadMine(); }, []);

  // Handle return from Stripe Checkout (?session_id=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/subscriptions/status/${sid}`);
        if (data.status === "active") {
          toast.success("Abonnement activé ! Vos crédits ont été ajoutés.");
          window.history.replaceState({}, "", "/client/subscriptions");
          await loadMine();
          refresh?.();
          return;
        }
      } catch { /* noop */ }
      if (attempts++ < 6) setTimeout(poll, 2000);
      else { toast.info("Paiement en cours de traitement. Vos crédits apparaîtront sous peu."); window.history.replaceState({}, "", "/client/subscriptions"); }
    };
    toast.loading("Vérification du paiement…", { duration: 2000 });
    poll();
  }, []);

  const subscribe = async (plan) => {
    setBusy(plan.id);
    try {
      const { data } = await api.post("/subscriptions/checkout", { plan_id: plan.id, origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de démarrer l'abonnement.");
      setBusy(null);
    }
  };

  const manage = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/subscriptions/portal", { plan_id: "", origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Portail indisponible.");
      setPortalBusy(false);
    }
  };

  return (
    <AppShell mode="client">
      <div className="mb-8">
        <div className="font-mono text-xs text-aurora-cyan tracking-[0.3em] uppercase mb-2">{t("sub.kicker")}</div>
        <h1 className="font-display text-4xl font-bold uppercase tracking-tight">{t("sub.title")}</h1>
        <p className="mt-3 text-zinc-400 max-w-2xl text-sm">
          {t("sub.subtitle1")}<span className="text-signal-success font-semibold">{t("sub.subtitlePct")}</span>{t("sub.subtitle2")}
        </p>
      </div>

      {active && (
        <div data-testid="active-subscription-banner" className="glass rounded-md p-5 mb-8 flex flex-col sm:flex-row sm:items-center gap-4 ring-1 ring-signal-success/40">
          <div className="w-11 h-11 grid place-items-center rounded-sm bg-signal-success/15 text-signal-success shrink-0"><ShieldCheck className="w-5 h-5" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg font-bold uppercase tracking-tight">Abonnement {active.plan_name} actif</div>
            <div className="font-mono text-[11px] text-zinc-400">{active.credits} crédits / {active.interval === "year" ? "an" : "mois"} · {active.price.toLocaleString("fr-FR")}€{active.cancel_at_period_end ? " · annulation en fin de période" : ""}</div>
          </div>
          <button data-testid="manage-subscription-btn" onClick={manage} disabled={portalBusy}
            className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-sm border border-aurora-cyan/50 text-aurora-cyan hover:bg-aurora-cyan/10 font-display uppercase tracking-wide text-sm transition-colors disabled:opacity-60">
            {portalBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Settings className="w-4 h-4" />} Gérer mon abonnement
          </button>
        </div>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5 mb-12">
        {PLANS.map((p) => <PlanCard key={p.id} plan={p} onSubscribe={subscribe} busy={busy} t={t} isActive={active?.plan_id === p.id} />)}
      </div>

      <div className="glass rounded-md p-6 md:p-8" data-testid="compare-table">
        <h2 className="font-display text-2xl font-bold uppercase tracking-tight mb-1">{t("sub.compareTitle")}</h2>
        <p className="font-mono text-[11px] text-zinc-500 mb-6">{t("sub.compareSub")}</p>
        <div className="space-y-0">
          <div className="grid grid-cols-3 gap-4 pb-3 border-b border-white/10 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            <div>{t("sub.criterion")}</div><div className="text-aurora-cyan">IMPERIUM</div><div>{t("sub.competition")}</div>
          </div>
          {compare.map(([label, ours, theirs]) => (
            <div key={label} className="grid grid-cols-3 gap-4 py-3 border-b border-white/5 last:border-0 items-center text-sm">
              <div className="text-zinc-400">{label}</div>
              <div className="text-white font-medium flex items-center gap-1.5">
                {ours === true ? <Check className="w-4 h-4 text-signal-success" /> : ours}
              </div>
              <div className="text-zinc-500">{theirs}</div>
            </div>
          ))}
        </div>
      </div>

      <p className="text-center font-mono text-[11px] text-zinc-600 mt-6">
        {t("sub.footer")}
      </p>
    </AppShell>
  );
};
